import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  AlertCircle,
  ArrowLeft,
  Check,
  FileText,
  Mail
} from 'lucide-react';
import ModalShell from './ModalShell';
import { showToast } from '../utils/toast';
import { readJsonErrorResponse } from '../utils/portalHelpers';

const EMAIL_ALERT_PORTAL_IDS = ['nfe', 'nfce', 'mdfe', 'cte', 'cte_svrs', 'nfe_svrs'];
const EMAIL_ALERT_TYPE_ORDER = ['service_availability', 'new_document'];
const EMAIL_ALERT_TYPE_META = {
  service_availability: {
    label: 'Disponibilidade do serviço',
    description: 'Receba alertas quando um portal ficar disponível ou indisponível.'
  },
  new_document: {
    label: 'Novos documentos',
    description: 'Receba alertas quando novos documentos forem publicados no portal.'
  }
};
const EMAIL_ALERT_PORTAL_LABELS = {
  nfe: 'NF-e SEFAZ',
  nfce: 'NFC-e SVRS',
  mdfe: 'MDF-e SVRS',
  cte: 'CT-e SEFAZ',
  cte_svrs: 'CT-e SVRS',
  nfe_svrs: 'NF-e SVRS'
};

function buildFallbackEmailTopics(portals) {
  return EMAIL_ALERT_TYPE_ORDER.flatMap((topicType) => (
    EMAIL_ALERT_PORTAL_IDS
      .filter((portalId) => portals[portalId])
      .map((portalId) => ({
        id: `${topicType}:${portalId}`,
        topicType,
        topicLabel: EMAIL_ALERT_TYPE_META[topicType]?.label || topicType,
        portalSlug: portalId,
        portalName: portals[portalId].label,
        sistema: portalId.split('_')[0].toUpperCase(),
        source: 'frontend_fallback',
        targetUrl: portals[portalId].targetUrl
      }))
  ));
}

// Etapa 1: digitar e-mail; Etapa 2: selecionar tópicos
const STEP_EMAIL = 'email';
const STEP_TOPICS = 'topics';

export default function EmailAlertsPanel({
  apiToken,
  buildRequestHeaders,
  getApiUrl,
  isAdminAuthenticated,
  onClose,
  portals,
  isTab = false
}) {
  const fallbackTopics = useMemo(() => buildFallbackEmailTopics(portals), [portals]);

  // Etapa atual do fluxo
  const [step, setStep] = useState(STEP_EMAIL);

  // Dados do formulário — sem persistência em localStorage
  const [subscriptionEmail, setSubscriptionEmail] = useState('');
  const [subscriptionName, setSubscriptionName] = useState('');
  const [selectedEmailTopics, setSelectedEmailTopics] = useState([]);

  // Tópicos disponíveis
  const [emailTopics, setEmailTopics] = useState(fallbackTopics);
  const [emailTopicsError, setEmailTopicsError] = useState('');
  const [isLoadingEmailTopics, setIsLoadingEmailTopics] = useState(false);

  // Estados de submissão
  const [isSavingEmailSubscription, setIsSavingEmailSubscription] = useState(false);
  const [isSendingEmailTests, setIsSendingEmailTests] = useState(false);

  const loadEmailTopics = useCallback(async () => {
    setIsLoadingEmailTopics(true);
    setEmailTopicsError('');

    try {
      const response = await fetch(getApiUrl('fiscal-email-topics'), {
        headers: buildRequestHeaders()
      });
      const payload = await readJsonErrorResponse(response);

      if (!response.ok) {
        throw new Error(payload?.error || 'Falha ao carregar as opções de alerta.');
      }

      const topics = Array.isArray(payload?.topics) && payload.topics.length > 0
        ? payload.topics
        : fallbackTopics;

      setEmailTopics(topics);
    } catch {
      setEmailTopics(fallbackTopics);
      setEmailTopicsError('Não foi possível carregar as opções da API. Exibindo uma lista compatível local.');
    } finally {
      setIsLoadingEmailTopics(false);
    }
  }, [buildRequestHeaders, fallbackTopics, getApiUrl]);

  useEffect(() => {
    loadEmailTopics();
  }, [loadEmailTopics]);

  // Ao mudar os tópicos disponíveis, filtra os selecionados para remover inválidos
  useEffect(() => {
    const availableTopicIds = new Set(emailTopics.map((topic) => topic.id));
    setSelectedEmailTopics((current) => current.filter((id) => availableTopicIds.has(id)));
  }, [emailTopics]);

  const groupedEmailTopics = useMemo(() => (
    EMAIL_ALERT_TYPE_ORDER
      .map((topicType) => ({
        topicType,
        label: EMAIL_ALERT_TYPE_META[topicType]?.label || topicType,
        description: EMAIL_ALERT_TYPE_META[topicType]?.description || '',
        topics: emailTopics.filter((topic) => topic.topicType === topicType)
      }))
      .filter((group) => group.topics.length > 0)
  ), [emailTopics]);

  const selectedServiceTestTopic = useMemo(() => (
    emailTopics.find((topic) => (
      topic.topicType === 'service_availability' &&
      selectedEmailTopics.includes(topic.id)
    )) || null
  ), [emailTopics, selectedEmailTopics]);

  const selectedDocumentTestTopic = useMemo(() => (
    emailTopics.find((topic) => (
      topic.topicType === 'new_document' &&
      selectedEmailTopics.includes(topic.id)
    )) || null
  ), [emailTopics, selectedEmailTopics]);

  const canSendAdminEmailTests = Boolean(
    isAdminAuthenticated &&
    subscriptionEmail.trim() &&
    selectedServiceTestTopic &&
    selectedDocumentTestTopic
  );

  const toggleEmailTopic = useCallback((topicId) => {
    setSelectedEmailTopics((current) => (
      current.includes(topicId)
        ? current.filter((item) => item !== topicId)
        : [...current, topicId]
    ));
  }, []);

  const toggleEmailTopicGroup = useCallback((topicType) => {
    const groupTopicIds = emailTopics
      .filter((topic) => topic.topicType === topicType)
      .map((topic) => topic.id);

    if (groupTopicIds.length === 0) return;

    setSelectedEmailTopics((current) => {
      const hasAllSelected = groupTopicIds.every((id) => current.includes(id));
      if (hasAllSelected) {
        return current.filter((id) => !groupTopicIds.includes(id));
      }
      return Array.from(new Set([...current, ...groupTopicIds]));
    });
  }, [emailTopics]);

  // Avança da etapa de e-mail para seleção de tópicos
  const handleEmailStepNext = useCallback((event) => {
    event.preventDefault();
    const trimmed = subscriptionEmail.trim();
    if (!trimmed || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      showToast('Informe um e-mail válido para continuar.', { type: 'warning' });
      return;
    }
    setStep(STEP_TOPICS);
  }, [subscriptionEmail]);

  const saveEmailSubscription = useCallback(async () => {
    if (!subscriptionEmail.trim()) {
      showToast('Informe o e-mail para salvar a inscrição.', { type: 'warning' });
      return;
    }

    if (selectedEmailTopics.length === 0) {
      showToast('Selecione pelo menos um alerta para continuar.', { type: 'warning' });
      return;
    }

    setIsSavingEmailSubscription(true);

    try {
      const response = await fetch(getApiUrl('fiscal-email-subscriptions'), {
        method: 'POST',
        headers: buildRequestHeaders({ contentType: 'application/json' }),
        body: JSON.stringify({
          email: subscriptionEmail.trim(),
          name: subscriptionName.trim(),
          topics: selectedEmailTopics
        })
      });
      const payload = await readJsonErrorResponse(response);

      if (!response.ok) {
        throw new Error(payload?.error || 'Falha ao salvar a inscrição.');
      }

      showToast('Inscrição salva! Você receberá os alertas selecionados por e-mail.', { type: 'success' });
      onClose();
    } catch (error) {
      showToast(`Falha ao salvar inscrição: ${error.message}`, { type: 'error' });
    } finally {
      setIsSavingEmailSubscription(false);
    }
  }, [buildRequestHeaders, getApiUrl, onClose, selectedEmailTopics, subscriptionEmail, subscriptionName]);

  const sendEmailTests = useCallback(async () => {
    if (!isAdminAuthenticated) {
      showToast('Valide um token administrativo antes de disparar testes reais.', { type: 'warning' });
      return;
    }

    if (!selectedServiceTestTopic || !selectedDocumentTestTopic) {
      showToast('Selecione ao menos um alerta de disponibilidade e um de novos documentos para testar os dois cenários.', { type: 'warning' });
      return;
    }

    setIsSendingEmailTests(true);

    try {
      const recipientEmail = subscriptionEmail.trim().toLowerCase();
      const recipientName = subscriptionName.trim();
      const servicePortalLabel = EMAIL_ALERT_PORTAL_LABELS[selectedServiceTestTopic.portalSlug] || selectedServiceTestTopic.portalName;
      const documentPortalLabel = EMAIL_ALERT_PORTAL_LABELS[selectedDocumentTestTopic.portalSlug] || selectedDocumentTestTopic.portalName;
      const checkedAt = new Date().toISOString();
      const headers = buildRequestHeaders({ admin: true, contentType: 'application/json' });

      const serviceResponse = await fetch(getApiUrl('fiscal-email-notifications'), {
        method: 'POST',
        headers,
        body: JSON.stringify({
          eventType: 'service_availability',
          testEmail: recipientEmail,
          testName: recipientName,
          service: {
            portalSlug: selectedServiceTestTopic.portalSlug,
            portalName: selectedServiceTestTopic.portalName,
            sistema: selectedServiceTestTopic.sistema,
            available: true,
            title: `[TESTE] Disponibilidade em ${servicePortalLabel}`,
            message: 'Este é um envio de teste com dados mockados para validar a entrega do alerta de disponibilidade.',
            checkedAt,
            details: 'Disparo manual com payload simulado pelo painel administrativo do Monitor Fiscal.'
          }
        })
      });
      const servicePayload = await readJsonErrorResponse(serviceResponse);

      if (!serviceResponse.ok) {
        throw new Error(servicePayload?.error || 'Falha ao disparar o teste de disponibilidade.');
      }

      const documentResponse = await fetch(getApiUrl('fiscal-email-notifications'), {
        method: 'POST',
        headers,
        body: JSON.stringify({
          eventType: 'new_document',
          testEmail: recipientEmail,
          testName: recipientName,
          document: {
            portalSlug: selectedDocumentTestTopic.portalSlug,
            portalName: selectedDocumentTestTopic.portalName,
            sistema: selectedDocumentTestTopic.sistema,
            tipo: 'Teste manual',
            nome: `[TESTE] Novos documentos em ${documentPortalLabel}`,
            url: window.location.href,
            data: new Date().toLocaleDateString('pt-BR')
          }
        })
      });
      const documentPayload = await readJsonErrorResponse(documentResponse);

      if (!documentResponse.ok) {
        throw new Error(documentPayload?.error || 'Falha ao disparar o teste de novos documentos.');
      }

      showToast(`Testes enviados para ${recipientEmail}. Verifique sua caixa de entrada.`, { type: 'success' });
    } catch (error) {
      showToast(`Falha ao enviar testes: ${error.message}`, { type: 'error' });
    } finally {
      setIsSendingEmailTests(false);
    }
  }, [
    buildRequestHeaders,
    getApiUrl,
    isAdminAuthenticated,
    selectedDocumentTestTopic,
    selectedServiceTestTopic,
    subscriptionEmail,
    subscriptionName
  ]);

  const renderContent = () => (
    <div className={`${isTab ? '' : 'p-4 sm:p-6 max-h-[80vh] overflow-y-auto bg-gradient-to-br from-emerald-50 via-cyan-50 to-white dark:from-emerald-950/25 dark:via-cyan-950/20 dark:to-gray-900'}`}>

      {/* ── ETAPA 1: E-mail ── */}
      {step === STEP_EMAIL && (
        <form onSubmit={handleEmailStepNext} className="space-y-5">
          <div className="rounded-2xl border border-emerald-100 dark:border-emerald-900/40 bg-white/80 dark:bg-gray-900/40 p-5 shadow-sm space-y-4">
            <div>
              <p className="text-[10px] font-black uppercase tracking-widest text-emerald-600 dark:text-emerald-400 mb-1">Seu e-mail</p>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Ao reenviar o mesmo e-mail com novas preferências, sua inscrição será atualizada automaticamente.
              </p>
            </div>

            <input
              type="email"
              value={subscriptionEmail}
              onChange={(e) => setSubscriptionEmail(e.target.value)}
              placeholder="seuemail@empresa.com.br"
              autoFocus
              required
              className="w-full h-11 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-2xl px-4 text-sm font-medium focus:ring-2 focus:ring-emerald-500 outline-none transition-all"
            />

            <input
              type="text"
              value={subscriptionName}
              onChange={(e) => setSubscriptionName(e.target.value)}
              placeholder="Seu nome ou equipe (opcional)"
              className="w-full h-11 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-2xl px-4 text-sm font-medium focus:ring-2 focus:ring-emerald-500 outline-none transition-all"
            />

            <button
              type="submit"
              className="w-full h-11 rounded-2xl bg-emerald-600 text-white text-[10px] font-black uppercase tracking-widest shadow-lg shadow-emerald-600/20 hover:bg-emerald-700 transition-colors"
            >
              Continuar →
            </button>
          </div>

          <p className="text-center text-xs text-gray-400 dark:text-gray-500">
            Para cancelar a inscrição, use o link enviado no rodapé de cada e-mail de alerta.
          </p>
        </form>
      )}

      {/* ── ETAPA 2: Seleção de tópicos ── */}
      {step === STEP_TOPICS && (
        <div className="space-y-4">
          {/* Cabeçalho da etapa */}
          <div className="flex items-center gap-3 rounded-2xl border border-emerald-100 dark:border-emerald-900/40 bg-white/80 dark:bg-gray-900/40 p-4 shadow-sm">
            <button
              type="button"
              onClick={() => setStep(STEP_EMAIL)}
              className="h-8 w-8 rounded-xl bg-gray-100 dark:bg-gray-800 flex items-center justify-center text-gray-500 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors shrink-0"
              title="Voltar"
            >
              <ArrowLeft size={14} />
            </button>
            <div className="min-w-0">
              <p className="text-[10px] font-black uppercase tracking-widest text-emerald-600 dark:text-emerald-400">Inscrevendo</p>
              <p className="text-sm font-medium text-gray-700 dark:text-gray-200 truncate">{subscriptionEmail}</p>
            </div>
          </div>

          {/* Lista de tópicos */}
          <div className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white/80 dark:bg-gray-900/40 p-4 shadow-sm space-y-4">
            <div>
              <p className="text-[10px] font-black uppercase tracking-widest text-gray-400 dark:text-gray-500 mb-1">Escolha os alertas</p>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {selectedEmailTopics.length === 0
                  ? 'Nenhum alerta selecionado'
                  : `${selectedEmailTopics.length} alerta${selectedEmailTopics.length > 1 ? 's' : ''} selecionado${selectedEmailTopics.length > 1 ? 's' : ''}`}
              </p>
            </div>

            {emailTopicsError ? (
              <div className="rounded-xl border border-amber-200 dark:border-amber-900/40 bg-amber-50 dark:bg-amber-900/10 p-3 text-xs text-amber-800 dark:text-amber-200">
                {emailTopicsError}
              </div>
            ) : null}

            {isLoadingEmailTopics ? (
              <div className="space-y-3 animate-pulse">
                {[1, 2].map((i) => (
                  <div key={i} className="rounded-2xl border border-gray-100 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/30 p-4 space-y-3">
                    <div className="h-3 w-32 bg-gray-200 dark:bg-gray-700 rounded" />
                    <div className="grid gap-2 sm:grid-cols-2">
                      {[1, 2, 3, 4].map((j) => (
                        <div key={j} className="h-14 rounded-2xl bg-gray-200 dark:bg-gray-700" />
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="space-y-4">
                {groupedEmailTopics.map((group) => {
                  const isGroupFullySelected = group.topics.every((topic) => selectedEmailTopics.includes(topic.id));
                  const GroupIcon = group.topicType === 'service_availability' ? AlertCircle : FileText;

                  return (
                    <div key={group.topicType} className="rounded-2xl border border-gray-100 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/30 p-4 space-y-3">
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex items-center gap-2">
                          <GroupIcon className={`h-4 w-4 ${group.topicType === 'service_availability' ? 'text-amber-500' : 'text-sky-500'}`} />
                          <div>
                            <p className="text-xs font-black uppercase tracking-widest text-gray-700 dark:text-gray-200">{group.label}</p>
                            <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5">{group.description}</p>
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => toggleEmailTopicGroup(group.topicType)}
                          className="shrink-0 text-[10px] font-black uppercase tracking-widest text-emerald-600 dark:text-emerald-400 whitespace-nowrap"
                        >
                          {isGroupFullySelected ? 'Limpar' : 'Todos'}
                        </button>
                      </div>

                      <div className="grid gap-2 sm:grid-cols-2">
                        {group.topics.map((topic) => {
                          const isSelected = selectedEmailTopics.includes(topic.id);

                          return (
                            <button
                              key={topic.id}
                              type="button"
                              aria-pressed={isSelected}
                              onClick={() => toggleEmailTopic(topic.id)}
                              className={`text-left rounded-xl border p-3 transition-all ${isSelected
                                ? 'border-emerald-300 bg-emerald-50 dark:border-emerald-700 dark:bg-emerald-900/20'
                                : 'border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800 hover:border-emerald-200 dark:hover:border-emerald-800'
                                }`}
                            >
                              <div className="flex items-center justify-between gap-2">
                                <p className={`text-xs font-bold ${isSelected ? 'text-emerald-700 dark:text-emerald-300' : 'text-gray-700 dark:text-gray-200'}`}>
                                  {EMAIL_ALERT_PORTAL_LABELS[topic.portalSlug] || topic.portalName}
                                </p>
                                <div className={`h-4 w-4 rounded-full border flex items-center justify-center shrink-0 ${isSelected ? 'border-emerald-500 bg-emerald-500 text-white' : 'border-gray-300 dark:border-gray-600 text-transparent'}`}>
                                  <Check className="h-2.5 w-2.5" />
                                </div>
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Botão salvar */}
          <button
            onClick={saveEmailSubscription}
            disabled={isSavingEmailSubscription || selectedEmailTopics.length === 0}
            className="w-full h-11 rounded-2xl bg-emerald-600 text-white text-[10px] font-black uppercase tracking-widest disabled:opacity-50 shadow-lg shadow-emerald-600/20 hover:bg-emerald-700 transition-colors"
          >
            {isSavingEmailSubscription ? 'Salvando...' : `Salvar inscrição (${selectedEmailTopics.length} alerta${selectedEmailTopics.length !== 1 ? 's' : ''})`}
          </button>

          {/* Painel de teste admin */}
          {isAdminAuthenticated ? (
            <div className="rounded-2xl border border-sky-200 dark:border-sky-900/40 bg-sky-50/80 dark:bg-sky-900/10 p-4 space-y-3">
              <div className="flex items-center gap-2">
                <Mail className="h-4 w-4 text-sky-600 dark:text-sky-300 shrink-0" />
                <p className="text-[10px] font-black uppercase tracking-widest text-sky-700 dark:text-sky-300">Teste operacional</p>
              </div>
              <p className="text-xs text-sky-900/80 dark:text-sky-100/80">
                Disparará e-mails reais de teste para <strong>{subscriptionEmail}</strong>.
              </p>
              <button
                onClick={sendEmailTests}
                disabled={!canSendAdminEmailTests || isSendingEmailTests}
                className="w-full h-10 rounded-2xl bg-sky-600 text-white text-[10px] font-black uppercase tracking-widest disabled:opacity-50 shadow-lg shadow-sky-600/20"
              >
                {isSendingEmailTests ? 'Enviando testes...' : 'Enviar testes de e-mail'}
              </button>
            </div>
          ) : null}
        </div>
      )}
    </div>
  );

  if (isTab) {
    return renderContent();
  }

  return (
    <ModalShell
      icon={Mail}
      maxWidthClass="max-w-2xl"
      onClose={onClose}
      subtitle="Receba notificações automáticas quando houver novidades nos portais fiscais."
      themeClass="bg-gradient-to-r from-emerald-600 via-teal-500 to-cyan-500"
      title="Alertas por E-mail"
    >
      {renderContent()}
    </ModalShell>
  );
}
